import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Customer, Quote, QuoteItem, UserProfile } from '../../types';
import { supabase } from '../../services/supabaseClient';
import { AcceptQuoteValues, hourlyQuoteItems, quoteAcceptDefaults, typzifferFuerProjektTyp } from '../../services/quoteAcceptDefaults';
import type { AnlageTyp } from '../../services/projektAnlageRechte';
import { isProjectNumberTaken, nextProjectNumber } from '../../services/crmService';
import { activeQuoteItems, typeDigitFromDocumentNumber } from '../../services/quoteLogic';
import { buildCoordinatorOptions, formatDistanceKm } from '../../services/nearbyProjects';
import { planungsPersonenAusIds, profilName, waehlbarFuerPlanung } from '../../services/planungsZuweisung';
import { auftragswertCentsAusAngebot, auftragswertCentsAusEingabe, centsAlsEingabe } from '../../services/erpSteering';
import {
  computeTrancheNet,
  formatEur,
  initialOrderSelection,
  linesFromSelection,
  openOrderLines,
  OrderSelection,
  parseOrderQty,
  statusAfterLines,
  validateOrderLines,
} from '../../services/quoteOrderTranches';
import { OrderLinesPicker } from './OrderLinesPicker';
import { AcceptFrameworkForm } from './AcceptFrameworkForm';
import { SupplementAcceptForm } from './SupplementAcceptForm';
import { AcceptFrameworkValues, isUnitPriceQuote } from '../../services/frameworkContractLogic';
import { isSupplementQuote, supplementLabel } from '../../services/quoteSupplementLogic';
import type { SupplementFormValues } from '../../services/supplementAcceptForm';
import { applyMinHoursInput } from '../../services/serviceUnits';
import { useNearbyProjects } from '../../hooks/useNearbyProjects';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { ohneGesperrte } from '../../services/gesperrteKonten';
import { NearbyProjectsPanel } from '../NearbyProjectsPanel';
import { Button } from '../Button';
import { Loader2, X, AlertTriangle, MapPin } from 'lucide-react';

interface Props {
  quote: Quote;
  customer: Customer;
  busy?: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: (values: AcceptQuoteValues, coordinator: UserProfile) => void;
  /**
   * Annahme als Rahmenvertrag (ohne Projekt). Nur gesetzt, wenn der Aufrufer
   * das kann; angeboten wird der Modus nur bei Angeboten ohne Endsumme.
   */
  onConfirmFramework?: (values: AcceptFrameworkValues) => void;
  /**
   * Annahme als NACHTRAG zu einem bestehenden Projekt (kein neues Projekt).
   * Nur gesetzt, wenn der Aufrufer das kann; `actor` ergaenzt der Aufrufer.
   * Bei einem Nachtragsangebot ist der Weg vorgewaehlt.
   */
  onConfirmSupplement?: (values: SupplementFormValues) => void;
  /** Moduswechsel — der Aufrufer raeumt die Fehlermeldung des anderen Wegs ab. */
  onModeChange?: (mode: AcceptMode) => void;
  /**
   * Projekttypen, die der Annehmende anlegen darf (erlaubteProjektTypen).
   * Fehlt = alle drei; die Datenbank (Nr. 154) prueft ohnehin.
   */
  projectTypes?: readonly AnlageTyp[];
}

type AcceptMode = 'framework' | 'project' | 'supplement';

const ALLE_PROJEKT_TYPEN: readonly AnlageTyp[] = ['sigeko', 'sifa', 'hse'];
const PROJEKT_TYP_LABEL: Readonly<Record<AnlageTyp, string>> = { sigeko: 'SiGeKo', sifa: 'SiFa', hse: 'HSE' };

const inputClass =
  'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

export const AcceptQuoteDialog: React.FC<Props> = ({ quote, customer, busy, error, onCancel, onConfirm, onConfirmFramework, onConfirmSupplement, onModeChange, projectTypes }) => {
  // Angebot ohne Endsumme: Rahmenvertrag ODER Projekt — BEWUSST ohne
  // Vorwahl. Bis 19.10.2026 war der Rahmenvertrag vorgewaehlt; A-26101010
  // (SiFa-Betreuung) wurde so durchgeklickt, und es entstand kein Projekt,
  // das jemand haette sehen koennen. Der Zustand des Projektformulars lebt
  // hier weiter (Profile, Nummernvorschlag, Eingaben) — beim Zurueckschalten
  // ist nichts verloren.
  // Nachtragsangebot (details.nachtragZu): der Nachtragsweg ist vorgewaehlt —
  // das Angebot wurde ausdruecklich als Nachtrag angelegt. „Projekt anlegen"
  // bleibt waehlbar, mit Warnhinweis.
  const frameworkAvailable = isUnitPriceQuote(quote) && !!onConfirmFramework;
  const supplementAvailable = !!onConfirmSupplement;
  const supplementQuote = isSupplementQuote(quote);
  const [mode, setMode] = useState<AcceptMode | null>(() =>
    (supplementAvailable && supplementQuote ? 'supplement' : frameworkAvailable ? null : 'project'));
  const isFramework = frameworkAvailable && mode === 'framework';
  const isSupplement = supplementAvailable && mode === 'supplement';
  // Ohne Wahlmoeglichkeit steht der Modus fest auf 'project' (Vorgabe oben).
  const isProject = mode === 'project';
  // Das Nachtragsformular sucht beim Einhaengen Projekte — erst einhaengen,
  // wenn der Weg einmal gewaehlt war; danach montiert lassen wie die anderen.
  const [supplementMounted, setSupplementMounted] = useState(isSupplement);
  const switchMode = (next: AcceptMode) => {
    if (busy || next === mode) return;
    if (next === 'supplement') setSupplementMounted(true);
    setMode(next);
    onModeChange?.(next);
  };
  const modeChoices: { key: AcceptMode; title: string; hint: string }[] = [];
  if (frameworkAvailable) {
    modeChoices.push({ key: 'framework', title: 'Nur Rahmenvertrag — kein Projekt', hint: 'Preise beim Kunden hinterlegen. Es entsteht KEIN Projekt; Projekte später über „Neues Projekt“ mit diesen Preisen.' });
  }
  modeChoices.push({
    key: 'project',
    title: 'Projekt anlegen',
    hint: supplementQuote
      ? 'Legt ein zweites, eigenes Projekt an — für einen Nachtrag in der Regel falsch.'
      : 'Projekt mit LV aus den Positionen — auch für SiFa-Betreuung oder HSE.',
  });
  if (supplementAvailable) {
    modeChoices.push({ key: 'supplement', title: 'Nachtrag zu bestehendem Projekt', hint: 'Kein neues Projekt: Mengen vorhandener Positionen erhöhen bzw. Positionen mit N-Nummer anhängen.' });
  }

  const [values, setValues] = useState<AcceptQuoteValues>(() =>
    quoteAcceptDefaults(quote, customer, new Date().toISOString()));
  const [users, setUsers] = useState<UserProfile[]>([]);
  // Gesperrte Mitglieder (Nr. 148) werden nicht mehr angeboten.
  const gesperrteKonten = useGesperrteKonten();
  const [numberTaken, setNumberTaken] = useState(false);
  const [checkingNumber, setCheckingNumber] = useState(false);

  // Auftragswert als eigener Textzustand: waehrend der Eingabe stehen dort
  // Zwischenstaende ("1.2", "1.234,"), die noch keine Zahl sind. Wuerde das
  // Feld direkt aus `values.orderValueCents` gespeist, loeschte jede solche
  // Zwischenstufe die Eingabe unter dem Cursor weg.
  const [orderValueText, setOrderValueText] = useState(() => centsAlsEingabe(values.orderValueCents));
  const orderValueCents = auftragswertCentsAusEingabe(orderValueText);
  // Von Hand geaendert? Dann folgt der Wert nicht mehr dem Umfang unten.
  const [orderValueTouched, setOrderValueTouched] = useState(false);

  // ─── Umfang der Beauftragung (Teilbeauftragung) ────────────────────────────
  // 'full' = wie bisher alles; 'partial' = Auswahl je Position/Menge. Nur die
  // gewaehlten Mengen gehen ins Projekt-LV, der Rest bleibt am Angebot offen
  // und laesst sich spaeter dort beauftragen.
  const fullLines = useMemo(() => openOrderLines(quote), [quote]);
  const canPartial = fullLines.length > 0;
  const [scope, setScope] = useState<'full' | 'partial'>('full');
  const [selection, setSelection] = useState<OrderSelection>(() => initialOrderSelection(quote, true));
  const partialLines = useMemo(() => linesFromSelection(selection), [selection]);
  const scopeLines = scope === 'partial' ? partialLines : fullLines;
  const scopeErrors = useMemo(
    () => (scope === 'partial' ? validateOrderLines(quote, partialLines) : []),
    [scope, quote, partialLines],
  );
  // Die Pruefung oben deckt "zu viel" und "nichts gewaehlt" ab, nicht aber
  // einen angehakten Posten mit unlesbarer Menge — der fiele still heraus.
  const unreadableQty = scope === 'partial'
    && Object.values(selection).some((v) => v.on && !((parseOrderQty(v.text) ?? 0) > 0));
  const scopeNet = useMemo(
    () => (canPartial && scopeLines.length > 0 ? computeTrancheNet(quote, scopeLines) : quote.netTotal),
    [canPartial, quote, scopeLines],
  );
  const becomesFull = scope === 'partial' && scopeErrors.length === 0 && statusAfterLines(quote, partialLines) === 'accepted';

  // Auftragswert folgt dem Umfang, solange niemand ihn von Hand geaendert hat.
  useEffect(() => {
    if (!orderValueTouched) setOrderValueText(centsAlsEingabe(auftragswertCentsAusAngebot(scopeNet)));
  }, [scopeNet, orderValueTouched]);

  // Umfeld-Check wie in der normalen Projektanlage: Baustellenadresse verorten
  // und zeigen, welcher Koordinator schon in der Naehe arbeitet.
  const nearby = useNearbyProjects();

  const set = <K extends keyof AcceptQuoteValues>(k: K, v: AcceptQuoteValues[K]) =>
    setValues((prev) => ({ ...prev, [k]: v }));

  // Projekttyp: Pflichtwahl. Es gilt nur, was angeboten wird (Rechte koennen
  // nach dem Oeffnen nachladen); darf jemand genau einen Typ anlegen, ist er
  // gewaehlt. Vorbelegt nur aus eindeutiger Angebotsnummer (quoteAcceptDefaults).
  const typen = projectTypes ?? ALLE_PROJEKT_TYPEN;
  const projectType: AnlageTyp | undefined =
    values.projectType && typen.includes(values.projectType)
      ? values.projectType
      : typen.length === 1 ? typen[0] : undefined;
  const isSifa = projectType === 'sifa';

  // Typziffer der Projektnummer: die des gewaehlten Typs (wie im
  // Anlage-Assistenten); solange keiner gewaehlt ist, die der Angebotsnummer
  // (A-JJMMTNNN), bei Altnummern ohne Typziffer SiGeKo ('1'). Das Nummernfeld
  // bleibt frei editierbar — die Eingabe des Nutzers gewinnt, auch gegen
  // einen spaeteren Typwechsel.
  const typeDigit = projectType
    ? typzifferFuerProjektTyp(projectType)
    : typeDigitFromDocumentNumber(quote.quoteNumber) ?? '1';
  // Von Hand eingetragen? Dann gewinnt die Eingabe (State fuer die Anzeige,
  // Ref fuer die Antworten laufender Anfragen).
  const [numberTouched, setNumberTouched] = useState(false);
  const numberTouchedRef = useRef(false);
  const numberRequestRef = useRef(0);
  // Fuer welche Typziffer der automatische Vorschlag im Feld steht. Weicht sie
  // vom gewaehlten Typ ab (Typ gewechselt, neue Nummer unterwegs), ist die
  // Nummer nicht bestaetigbar — sonst entstuende ein SiFa-Projekt mit der
  // Ziffer 1 (Codex-Review 19.10.).
  const [autoNumberDigit, setAutoNumberDigit] = useState<string | null>(null);
  const numberPending = !numberTouched && autoNumberDigit !== typeDigit;
  // Verfuegbarkeitspruefung: nur die Antwort fuer die Nummer, die noch im
  // Feld steht, zaehlt. Jede Aenderung der Nummer macht laufende Pruefungen
  // ungueltig.
  const checkRequestRef = useRef(0);
  const invalidateNumberCheck = () => {
    checkRequestRef.current += 1;
    setCheckingNumber(false);
    setNumberTaken(false);
  };

  useEffect(() => {
    supabase.from('profiles').select('*').then(({ data }) => { if (data) setUsers(data as UserProfile[]); });
  }, []);

  useEffect(() => {
    if (numberTouchedRef.current) return;
    // Nummernvorschlag serverseitig ziehen. Nur die Antwort der LETZTEN
    // Anfrage zaehlt — wer schnell den Typ wechselt, bekaeme sonst die Nummer
    // des vorigen Typs. Scheitert sie, wird das Feld geleert: eine Nummer mit
    // fremder Typziffer darf nicht stehen bleiben; der Nutzer traegt selbst
    // eine ein.
    const request = ++numberRequestRef.current;
    const apply = (n: string) => {
      if (request !== numberRequestRef.current || numberTouchedRef.current) return;
      setValues((prev) => ({ ...prev, projectNumber: n }));
      setAutoNumberDigit(typeDigit);
      // Neue Nummer im Feld: eine laufende Pruefung der alten zaehlt nicht.
      checkRequestRef.current += 1;
      setCheckingNumber(false);
      setNumberTaken(false);
    };
    nextProjectNumber(typeDigit).then(apply).catch(() => apply(''));
  }, [typeDigit]);

  // Einmalig beim Oeffnen mit der vorbelegten Adresse; danach nur noch beim
  // Verlassen des Adressfeldes, damit nicht jede Eingabe Nominatim trifft.
  useEffect(() => {
    void nearby.lookup(values.address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkNumber = async () => {
    if (!values.projectNumber) return;
    const request = ++checkRequestRef.current;
    setCheckingNumber(true);
    try {
      const taken = await isProjectNumberTaken(values.projectNumber);
      if (request === checkRequestRef.current) setNumberTaken(taken);
    } finally {
      if (request === checkRequestRef.current) setCheckingNumber(false);
    }
  };

  // Stundenpositionen des Angebots: nur fuer sie wird nach Mindeststunden
  // gefragt. Gibt es keine, entfaellt der ganze Abschnitt (kein leerer Block).
  // Gestrichene Positionen bleiben aussen vor — sie gehen gar nicht erst ins
  // Projekt-LV (mapQuoteItemsToServiceItems), eine Mindeststunden-Abfrage dazu
  // waere gegenstandslos.
  // Bei Teilbeauftragung nur die beauftragten — die anderen kommen gar nicht ins LV.
  const hourlyItems = useMemo(() => {
    const all = hourlyQuoteItems(activeQuoteItems<QuoteItem>(quote.items || []));
    if (scope !== 'partial') return all;
    const ids = new Set(partialLines.map((l) => l.itemId));
    return all.filter((it) => ids.has(it.id));
  }, [quote.items, scope, partialLines]);

  // Rohtext je Position — GETRENNT vom geparsten Modellwert, genau wie beim
  // Auftragswert daneben. Wuerde das Feld aus values.minHoursByItemId gespeist,
  // loeschte die Zwischenstufe "0" beim Tippen von "0.25" die Eingabe unter dem
  // Cursor weg, und die naechsten Ziffern ergaeben 25 statt 0.25.
  const [minHoursText, setMinHoursText] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const [id, hours] of Object.entries(values.minHoursByItemId || {})) initial[id] = String(hours);
    return initial;
  });

  const setMinHours = (itemId: string, raw: string) => {
    setMinHoursText((prev) => ({ ...prev, [itemId]: raw }));
    setValues((prev) => {
      const current = prev.minHoursByItemId || {};
      // Halb getippte Eingaben ("0", "0.") lassen den Wert stehen; nur ein
      // LEERES Feld heisst „keine Mindeststunden" — die Position verhaelt sich
      // dann wie bisher (Vorbelegung 1 bei der Buchung).
      const nextValue = applyMinHoursInput(raw, current[itemId]);
      if (nextValue === current[itemId]) return prev;
      const next = { ...current };
      if (nextValue !== undefined) next[itemId] = nextValue;
      else delete next[itemId];
      return { ...prev, minHoursByItemId: next };
    });
  };

  const coordinator = users.find((u) => u.id === values.coordinatorId);

  // Verantwortliche Planungsphase (docs/projekt-kickoff-plan.md §5): dieselben
  // Regeln wie im Anlage-Assistenten (services/planungsZuweisung.ts) — ohne
  // den Hauptverantwortlichen, ohne Dubletten, leer = er plant selbst.
  const planningUsers = useMemo(
    () => planungsPersonenAusIds(values.planningUserIds, users, values.coordinatorId || undefined),
    [values.planningUserIds, users, values.coordinatorId],
  );
  // Alphabetisch: die Profile kommen unsortiert aus der Datenbank, und in
  // einer ungeordneten Liste sucht man den Kollegen lange.
  const planningSelectable = useMemo(
    () => waehlbarFuerPlanung(ohneGesperrte(users, gesperrteKonten), values.coordinatorId || undefined, values.planningUserIds || [])
      .sort((a, b) => profilName(a).localeCompare(profilName(b), 'de')),
    [users, gesperrteKonten, values.coordinatorId, values.planningUserIds],
  );
  const addPlanningUser = (id: string) => {
    if (!id) return;
    setValues((prev) => {
      const ids = prev.planningUserIds || [];
      return ids.includes(id) ? prev : { ...prev, planningUserIds: [...ids, id] };
    });
  };
  const removePlanningUser = (id: string) =>
    setValues((prev) => ({ ...prev, planningUserIds: (prev.planningUserIds || []).filter((x) => x !== id) }));
  const setMainCoordinator = (id: string) =>
    setValues((prev) => ({
      ...prev,
      coordinatorId: id,
      // Wer Hauptverantwortlicher wird, plant ohnehin mit — als Planungsperson
      // stuende er doppelt in der Vertreterliste und bekaeme zwei Quittungen.
      planningUserIds: (prev.planningUserIds || []).filter((x) => x !== id),
    }));

  // Pflichtfeld ohne Vorauswahl: ohne aktive Wahl bleibt der Button gesperrt.
  const scopeOk = scope === 'full' || (scopeErrors.length === 0 && !unreadableQty);
  const canConfirm = !!projectType && !!values.projectName.trim() && !!values.projectNumber.trim() && !numberPending && !numberTaken && !busy && !!coordinator && scopeOk;

  const coordinatorOptions = useMemo(
    () => buildCoordinatorOptions(ohneGesperrte(users, gesperrteKonten, values.coordinatorId), nearby.projects),
    [users, gesperrteKonten, values.coordinatorId, nearby.projects],
  );

  const handleConfirm = () => {
    if (!coordinator || !projectType) return;
    // Koordinaten mitgeben, damit das neue Projekt selbst im Umfeld-Check
    // auftaucht (die Projektanlage ueber den Wizard macht dasselbe).
    // Leeres oder unlesbares Auftragswert-Feld => 0 => es wird nichts fixiert.
    onConfirm(
      {
        ...values,
        projectType,
        coordinates: nearby.coordinates || undefined,
        orderValueCents: orderValueCents ?? 0,
        // Ohne orderLines = vollstaendig (acceptQuote nimmt alle offenen Mengen).
        orderLines: scope === 'partial' ? partialLines : undefined,
        customerRef: values.customerRef?.trim() || undefined,
        // Genau die Personen, die als Chips zu sehen sind — bereinigt um den
        // Hauptverantwortlichen und um ids, die es nicht (mehr) gibt. SiFa
        // hat keine Planungsphase: der Abschnitt ist ausgeblendet, gewaehlte
        // Personen von vor dem Typwechsel gehen nicht mit.
        planningUserIds: isSifa ? [] : planningUsers.map((u) => u.id),
      },
      coordinator,
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 animate-fade-in backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{isFramework ? 'Rahmenvertrag hinterlegen' : isSupplement ? 'Nachtrag eintragen' : isProject ? 'Auftrag anlegen' : 'Angebot annehmen'}</h2>
            <p className="text-sm text-slate-500">
              {isFramework
                ? 'Es wird KEIN Projekt angelegt — nur die Preise werden beim Kunden hinterlegt.'
                : isSupplement
                  ? 'Es wird KEIN neues Projekt angelegt — die Positionen gehen in das LV eines bestehenden Projekts.'
                  : isProject
                    ? 'Angaben prüfen, bevor das Projekt erstellt wird.'
                    : 'Bitte zuerst wählen, was aus dem Angebot entsteht.'}
            </p>
          </div>
          <button onClick={onCancel} disabled={busy} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed" title={busy ? (isFramework ? 'Rahmenvertrag wird hinterlegt — bitte warten' : isSupplement ? 'Nachtrag wird eingetragen — bitte warten' : 'Projekt wird angelegt — bitte warten') : 'Abbrechen'}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Moduswahl bei Angeboten ohne Endsumme (ohne Vorwahl) und, wenn
            der Aufrufer ihn kann, mit dem Nachtragsweg. Waehrend die Annahme
            laeuft, gesperrt — das Ergebnis gehoert zum gestarteten Weg. */}
        {modeChoices.length > 1 && (
          <div className="px-6 pt-6">
          <label className={labelClass}>Was entsteht aus dem Angebot? *</label>
          <div className={`mt-1 grid grid-cols-1 gap-2 ${modeChoices.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {modeChoices.map(({ key, title, hint }) => (
              <button
                key={key}
                type="button"
                onClick={() => switchMode(key)}
                disabled={busy}
                aria-pressed={mode === key}
                className={`text-left rounded-xl border px-4 py-3 transition-all disabled:cursor-not-allowed ${mode === key ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-500/20' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <div className="text-sm font-bold text-slate-800">{title}</div>
                <div className="text-xs text-slate-500">{hint}</div>
              </button>
            ))}
          </div>
          {mode === null && (
            <p className="text-xs text-amber-700 mt-1 ml-1 font-semibold">
              Pflichtfeld — bitte aktiv auswählen, es gibt keine Vorbelegung.
            </p>
          )}
          </div>
        )}

        {/* Noch nichts gewaehlt: kein Formular, nur Abbrechen. */}
        {mode === null && (
          <div className="flex justify-end gap-3 p-6 mt-6 border-t border-slate-200">
            <Button variant="secondary" onClick={onCancel} disabled={busy}>Abbrechen</Button>
          </div>
        )}

        {/* Beide Formulare bleiben montiert, nur eins ist sichtbar: so gehen
            Eingaben beim Hin- und Herschalten nicht verloren. */}
        {frameworkAvailable && onConfirmFramework && (
          <div className={isFramework ? undefined : 'hidden'}>
            <AcceptFrameworkForm
              quote={quote}
              customer={customer}
              busy={busy}
              error={error}
              onCancel={onCancel}
              onConfirm={onConfirmFramework}
            />
          </div>
        )}

        {supplementAvailable && onConfirmSupplement && supplementMounted && (
          <div className={isSupplement ? undefined : 'hidden'}>
            <SupplementAcceptForm
              quote={quote}
              customer={customer}
              busy={busy}
              error={error}
              onCancel={onCancel}
              onConfirm={onConfirmSupplement}
            />
          </div>
        )}

        <div className={isProject ? undefined : 'hidden'}>
        <div className="p-6 space-y-4">
          {/* Nachtragsangebot ueber den Projektweg: entsteht ein ZWEITES
              Projekt — erlaubt, aber nicht ohne Hinweis. */}
          {supplementQuote && quote.details?.nachtragZu && (
            <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Dieses Angebot ist {supplementLabel(quote.details.nachtragZu)}. „Projekt anlegen“ legt ein zweites, eigenes Projekt an
                {supplementAvailable ? ' — für den Nachtrag bitte „Nachtrag zu bestehendem Projekt“ wählen.' : '.'}
              </span>
            </div>
          )}
          {/* Projekttyp — Pflichtwahl, nur die erlaubten Typen. SiFa legt das
              Projekt an wie der Anlage-Assistent (quoteToReport): laufend,
              ohne Begehungsrhythmus, ohne Planungsphase. */}
          <div>
            <label className={labelClass}>Projekttyp *</label>
            {typen.length === 0 ? (
              <p className="text-xs text-red-600 mt-1 ml-1 font-semibold">
                Ihnen fehlt das Recht, Projekte anzulegen. Ein Admin kann es unter Verwaltung → Benutzer &amp; Rechte → Rollen &amp; Rechte vergeben.
              </p>
            ) : (
              <>
                <div className="mt-1 flex flex-wrap gap-2">
                  {typen.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => set('projectType', t)}
                      disabled={busy}
                      aria-pressed={projectType === t}
                      className={`flex-1 min-w-[6rem] rounded-xl border px-3 py-2 text-sm font-bold transition-all disabled:cursor-not-allowed ${projectType === t ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-500/20 text-slate-800' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    >
                      {PROJEKT_TYP_LABEL[t]}
                    </button>
                  ))}
                </div>
                {!projectType ? (
                  <p className="text-xs text-amber-700 mt-1 ml-1 font-semibold">
                    Pflichtfeld — bitte aktiv auswählen, es gibt keine Vorbelegung.
                  </p>
                ) : isSifa ? (
                  <p className="text-xs text-slate-500 mt-1 ml-1">
                    SiFa-Betreuung: läuft unbefristet, ohne Begehungsrhythmus und ohne Planungsphase — wie im Anlage-Assistenten.
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div>
            <label className={labelClass}>Projektname</label>
            <input className={inputClass} value={values.projectName} onChange={(e) => set('projectName', e.target.value)} />
          </div>

          <div>
            <label className={labelClass}>Projektnummer</label>
            <input className={inputClass} value={values.projectNumber}
                   onChange={(e) => {
                     numberTouchedRef.current = true;
                     setNumberTouched(true);
                     set('projectNumber', e.target.value);
                     invalidateNumberCheck();
                   }}
                   onBlur={checkNumber} />
            {numberPending && <p className="text-xs text-slate-500 mt-1 ml-1">Nummernvorschlag für den gewählten Typ wird geladen…</p>}
            {!numberPending && !numberTouched && !values.projectNumber && (
              <p className="text-xs text-amber-700 mt-1 ml-1 font-semibold">Kein Nummernvorschlag — bitte selbst eintragen.</p>
            )}
            {checkingNumber && <p className="text-xs text-slate-500 mt-1 ml-1">Verfügbarkeit wird geprüft…</p>}
            {numberTaken && <p className="text-xs text-red-600 mt-1 ml-1">Diese Projektnummer ist bereits vergeben.</p>}
          </div>

          <div>
            <label className={labelClass}>{isSifa ? 'Betriebsadresse' : 'Baustellenadresse'}</label>
            <div className="relative">
              <input className={inputClass} value={values.address}
                     onChange={(e) => set('address', e.target.value)}
                     onBlur={() => { void nearby.lookup(values.address); }} />
              {nearby.geocoding ? (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
              ) : nearby.coordinates ? (
                <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              ) : null}
            </div>
            {customer.address && values.address !== customer.address && (
              <button onClick={() => { set('address', customer.address || ''); void nearby.lookup(customer.address || ''); }}
                      className="text-xs text-blue-600 hover:underline mt-1 ml-1">Kundenadresse übernehmen</button>
            )}
          </div>

          {/* Umfeld-Check direkt ueber der Auswahl: erst sehen, wer in der Naehe
              arbeitet, dann den Hauptverantwortlichen waehlen. */}
          <NearbyProjectsPanel
            checking={nearby.checking}
            projects={nearby.projects}
            geocodeFailed={nearby.geocodeFailed}
          />

          <div>
            <label className={labelClass}>{isSifa ? 'Hauptverantwortlicher (Betreuung, Projektbesitzer) *' : 'Hauptverantwortlicher (Ausführung, Projektbesitzer) *'}</label>
            <select
              className={`${inputClass} ${values.coordinatorId ? '' : 'border-amber-300 bg-amber-50/40'}`}
              value={values.coordinatorId}
              onChange={(e) => setMainCoordinator(e.target.value)}
            >
              <option value="">Bitte auswählen …</option>
              {coordinatorOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                  {o.distanceKm !== null ? ` · Projekt in ca. ${formatDistanceKm(o.distanceKm)} Entfernung` : ''}
                </option>
              ))}
            </select>
            {!values.coordinatorId && (
              <p className="text-xs text-amber-700 mt-1 ml-1 font-semibold">
                Pflichtfeld — bitte aktiv auswählen, es gibt keine Vorbelegung.
              </p>
            )}
          </div>

          {/* Verantwortliche Planungsphase — Bedienung wie im Anlage-Assistenten:
              Chips mit ×, darunter „+ Person hinzufuegen …". Mehrere Personen
              moeglich; leer = der Hauptverantwortliche plant selbst. Er selbst
              steht hier nicht zur Wahl. SiFa hat keine Planungsphase. */}
          {!isSifa && <div>
            <label className={labelClass}>Verantwortliche Planungsphase</label>
            {planningUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2 mt-1">
                {planningUsers.map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-bold rounded-full pl-2.5 pr-1 py-0.5">
                    {profilName(u)}
                    <button
                      type="button"
                      title="Entfernen"
                      onClick={() => removePlanningUser(u.id)}
                      disabled={busy}
                      className="p-0.5 rounded-full text-blue-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <select
              className={`${inputClass} disabled:text-slate-400`}
              value=""
              onChange={(e) => addPlanningUser(e.target.value)}
              disabled={busy || planningSelectable.length === 0}
            >
              <option value="">+ Person hinzufügen …</option>
              {planningSelectable.map((u) => (
                <option key={u.id} value={u.id}>{profilName(u)}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1 ml-1">
              {planningUsers.length === 0
                ? 'Leer = der Hauptverantwortliche plant selbst. '
                : 'Die Planungspersonen kommen als eigene Rolle in die Verantwortlichkeiten und bekommen die Planungstermine. '}
              Planung = Vorankündigung, Unterlage, SiGe-Plan, A+S-Plan, Planung allg. — keine Begehungen.
            </p>
          </div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{isSifa ? 'Betreuungsbeginn' : 'Beginn'}</label>
              <input type="date" className={inputClass} value={values.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>{isSifa ? 'Ende (leer = unbefristet)' : 'Ende'}</label>
              <input type="date" className={inputClass} value={values.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>

          {/* Umfang der Beauftragung. Vollstaendig ist die Vorgabe — wie bisher.
              Teilbeauftragung: nur die gewaehlten Mengen kommen ins Projekt,
              der Rest bleibt am Angebot offen ("Rest beauftragen"). */}
          {canPartial && (
            <div className="space-y-3">
              <label className={labelClass}>Umfang der Beauftragung</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {([
                  ['full', 'Vollständig beauftragt', 'Alle Positionen mit allen Mengen.'],
                  ['partial', 'Teilbeauftragung', 'Nur ein Teil — der Rest bleibt am Angebot offen.'],
                ] as const).map(([key, title, hint]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setScope(key)}
                    disabled={busy}
                    className={`text-left rounded-xl border px-4 py-3 transition-all ${scope === key ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-500/20' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className="text-sm font-bold text-slate-800">{title}</div>
                    <div className="text-xs text-slate-500">{hint}</div>
                  </button>
                ))}
              </div>
              {scope === 'partial' && (
                <>
                  <OrderLinesPicker quote={quote} selection={selection} onChange={setSelection} disabled={busy} />
                  <p className="text-sm text-slate-600">
                    Beauftragt <strong className="text-slate-800">{formatEur(scopeNet)}</strong>
                    {' · '}offen <strong className="text-slate-800">{formatEur(Math.max(0, quote.netTotal - scopeNet))}</strong>
                    <span className="text-slate-400"> (netto, Angebot {formatEur(quote.netTotal)})</span>
                  </p>
                  {partialLines.length === 0 ? (
                    <p className="text-xs font-semibold text-amber-700">Bitte mindestens eine Position mit Menge wählen.</p>
                  ) : scopeErrors.length > 0 ? (
                    <p className="text-xs font-semibold text-red-600">{scopeErrors.join(' ')}</p>
                  ) : null}
                  {becomesFull && (
                    <p className="text-xs text-slate-500">Alles gewählt — das Angebot wird als vollständig beauftragt angelegt.</p>
                  )}
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Bestellnummer Kunde</label>
                  <input className={inputClass} value={values.customerRef || ''} placeholder="optional"
                         onChange={(e) => set('customerRef', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Beauftragt am</label>
                  <input type="date" className={inputClass} value={values.orderDate || ''}
                         onChange={(e) => set('orderDate', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Auftragswert-Feststellung (ERP-Steuerung, Plan Abschnitt 8): der
              Auftragsbestand braucht eine fixierte Zahl je Projekt. Vorschlag
              ist die Angebots-Nettosumme; sie bleibt danach unabhaengig von
              spaeteren Angebotsaenderungen. Nachtragen geht jederzeit unter
              „Steuerung → Auftragswerte". */}
          <div>
            <label className={labelClass}>Auftragswert netto (EUR)</label>
            <input
              className={inputClass}
              inputMode="decimal"
              value={orderValueText}
              onChange={(e) => { setOrderValueTouched(true); setOrderValueText(e.target.value); }}
              placeholder="0,00"
            />
            <p className="text-xs text-slate-500 mt-1 ml-1">
              {orderValueText.trim() === ''
                ? 'Leer lassen — der Auftragswert kann später unter „Steuerung“ nachgetragen werden.'
                : orderValueCents === null
                  ? <span className="text-amber-700 font-semibold">Keine gültige Zahl — es wird kein Auftragswert fixiert.</span>
                  : scope === 'partial'
                    ? 'Vorschlag aus dem beauftragten Teil (netto). Wird beim Anlegen fixiert; spätere Teile erhöhen ihn.'
                    : 'Vorschlag aus der Angebotssumme (netto). Wird beim Anlegen fixiert und ist später änderbar.'}
            </p>
          </div>

          {/* SiFa-Betreuung hat keinen Begehungsrhythmus (quoteToReport laesst
              intervalConfig dort leer). */}
          {!isSifa && <div>
            <label className={labelClass}>Begehungsintervall</label>
            <select className={inputClass} value={values.intervalConfig.type}
                    onChange={(e) => set('intervalConfig', { ...values.intervalConfig, type: e.target.value as AcceptQuoteValues['intervalConfig']['type'] })}>
              <option value="daily">täglich</option>
              <option value="weekly">wöchentlich</option>
              <option value="biweekly">14-täglich</option>
              <option value="twice_monthly">2x monatlich</option>
              <option value="monthly">monatlich</option>
              <option value="custom">individuell</option>
              <option value="manual">manuell</option>
            </select>
          </div>}

          {/* Mindeststunden je Stundenposition. Das Angebot kennt sie nicht —
              gefragt wird hier, unmittelbar bevor das Projekt entsteht, damit
              die Buchungsdialoge spaeter eine Vorbelegung haben. Aenderbar
              bleibt der Wert danach unter „Nachtrag & Daten-Korrektur". */}
          {hourlyItems.length > 0 && (
            <div>
              <label className={labelClass}>Mindeststunden je Buchung</label>
              <div className="mt-1 border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
                {hourlyItems.map((it) => {
                  const unset = values.minHoursByItemId?.[it.id] === undefined;
                  return (
                    <div key={it.id} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-700 truncate">
                          <span className="font-mono text-xs text-slate-400 mr-2">{it.lvPosition}</span>
                          {it.title}
                        </div>
                        {unset && (
                          <div className="text-[11px] font-semibold text-amber-700 mt-0.5">
                            Mindeststunden festlegen
                          </div>
                        )}
                      </div>
                      <div className="relative w-28 shrink-0">
                        {/* NICHT type="number" — dasselbe wie beim Auftragswert oben:
                            ein Zahlenfeld leert e.target.value fuer jeden
                            Zwischenstand, der keine gueltige Gleitkommazahl ist
                            ("2.", "0,"), und der Handler koennte das nicht von
                            einem bewusst geleerten Feld unterscheiden. */}
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="1"
                          className={`${inputClass} pr-10 text-right ${unset ? 'border-amber-300 bg-amber-50/40' : ''}`}
                          value={minHoursText[it.id] ?? ''}
                          onChange={(e) => setMinHours(it.id, e.target.value)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400 pointer-events-none">
                          Std.
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-1">
                Vorschlag für die Leistungserfassung: so viele Stunden werden bei einer Buchung
                vorbelegt. Leer lassen ist möglich und heißt „keine Vorgabe“ — dann bleibt es
                bei einer Stunde, wie bisher. Korrigieren lässt sich der Wert bei jeder Buchung.
              </p>
            </div>
          )}

          {error && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Abbrechen</Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}
                  title={!projectType ? 'Bitte zuerst den Projekttyp auswählen.' : !coordinator ? 'Bitte zuerst den Hauptverantwortlichen auswählen.' : undefined}>
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Projekt wird angelegt…</> : 'Projekt anlegen'}
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
};
